"use client";

import { Download, Loader2, Pause, Play } from "lucide-react";
import React from "react";

import { cn, getProxyRecordingUrl, downloadRecording } from "@/app/_module/lib/utils";
import {
  PLAYBACK_RATES,
  useCallRecording,
  useCallRecordingClock,
  type CallRecording,
} from "@/app/_module/contexts/CallRecordingContext";
import { focusRing } from "@/app/(authenticated)/settings/a2p-verification/_components/overview/primitives";

/**
 * Inline call-recording player (lead History, the lead's call table,
 * Communication, Agent Details). It owns no audio: it's a view of the one
 * app-wide recording in CallRecordingContext, so playback carries on in the
 * CallRecordingBar when the agent leaves the row, tab or page. Accents use the
 * `primary` token so it follows white-label palettes.
 */

export const fmtClock = (secs: number) => {
  if (!Number.isFinite(secs) || secs < 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export function RecordingPlayer({ recording, downloadName }: { recording: CallRecording; downloadName?: string }) {
  const player = useCallRecording();
  const isCurrent = player?.current?.callId === recording.callId;
  const status = isCurrent ? player!.status : "paused";
  const playing = status === "playing";
  const rate = player?.rate ?? 1;
  const nextRate = PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(rate as (typeof PLAYBACK_RATES)[number]) + 1) % PLAYBACK_RATES.length];

  const togglePlay = () => {
    if (!player) return;
    if (isCurrent) player.toggle();
    else void player.play(recording);
  };
  const seekTo = (secs: number) => {
    if (!player) return;
    if (isCurrent && status !== "error") player.seek(secs);
    else void player.play(recording, secs);
  };

  return (
    <div className="flex items-center gap-2.5">
      <button
        type="button"
        onClick={togglePlay}
        disabled={!player || status === "loading"}
        title={player ? undefined : "Recording player unavailable here"}
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full shadow-sm transition-all",
          playing ? "bg-primary text-primary-foreground" : "bg-white text-primary ring-1 ring-inset ring-primary/25 hover:bg-primary/5",
          focusRing,
        )}
        aria-label={status === "error" ? "Try again" : playing ? "Pause" : "Play"}
      >
        {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
      </button>

      {isCurrent ? (
        <LiveProgress fallbackTotal={Number(recording.durationSecs) || 0} onSeek={seekTo} playing={playing} error={status === "error"} />
      ) : (
        <Progress time={0} total={Number(recording.durationSecs) || 0} onSeek={seekTo} playing={false} />
      )}

      <button
        type="button"
        onClick={() => player?.setRate(nextRate)}
        className={cn(
          "shrink-0 rounded-full px-2 py-1 text-[10px] font-bold tabular-nums ring-1 ring-inset transition-colors",
          rate !== 1 ? "bg-primary/10 text-primary ring-primary/20" : "bg-gray-50 text-gray-500 ring-gray-500/10 hover:text-gray-700",
          focusRing,
        )}
        title="Playback speed"
        aria-label={`Playback speed ${rate}x. Change to ${nextRate}x`}
      >
        {rate}x
      </button>
      <button
        type="button"
        onClick={() => {
          const proxyUrl = getProxyRecordingUrl(recording.recordingUrl) || recording.recordingUrl;
          void downloadRecording(proxyUrl, downloadName || "recording.mp3");
        }}
        className={cn("shrink-0 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700", focusRing)}
        title="Download recording"
        aria-label="Download recording"
      >
        <Download className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// Only the recording that's actually loaded subscribes to the ~4 Hz clock.
function LiveProgress({ fallbackTotal, onSeek, playing, error }: { fallbackTotal: number; onSeek: (s: number) => void; playing: boolean; error: boolean }) {
  const { time, duration } = useCallRecordingClock();
  return (
    <div className="min-w-0 flex-1">
      <Progress time={time} total={duration || fallbackTotal} onSeek={onSeek} playing={playing} />
      {error && <p className="mt-0.5 text-[10px] text-red-600">Couldn&apos;t load the recording. Press play to try again.</p>}
    </div>
  );
}

function Progress({ time, total, onSeek, playing }: { time: number; total: number; onSeek: (s: number) => void; playing: boolean }) {
  return (
    <div className="min-w-0 flex-1">
      <input
        type="range"
        min={0}
        max={total || 0}
        step={0.1}
        value={Math.min(time, total || 0)}
        disabled={!total}
        onChange={(e) => onSeek(Number(e.target.value))}
        aria-label="Position in recording"
        aria-valuetext={`${fmtClock(time)} of ${fmtClock(total)}`}
        className={cn("h-1.5 w-full cursor-pointer accent-primary disabled:cursor-default", focusRing)}
      />
      <div className="mt-0.5 flex items-center justify-between text-[10px] tabular-nums text-gray-500">
        <span className={playing ? "font-semibold text-primary" : ""}>{fmtClock(time)}</span>
        <span>{fmtClock(total)}</span>
      </div>
    </div>
  );
}
